import { useMutation, useQueryClient } from "@tanstack/react-query";
import { normalizeWorkLocationIds } from "@/features/employees/shared/staffWorkLocations";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { UsersApi } from "@/openapi/generated";
import type {
  GetLocationBaseResponse,
  UpdateUserRequest,
} from "@/openapi/generated/models";

export interface UseUserUpdateOptions {
  successTitle?: string;
  successDescription?: string;
}

export type UserUpdatePayload = UpdateUserRequest & {
  email: string;
};

export function withUserEmailInPutBody(
  body: unknown,
  email: string | null | undefined,
): unknown {
  if (email === undefined) return body;
  if (body && typeof body === "object" && !Array.isArray(body)) {
    return { ...body, email };
  }
  return { email };
}

function buildWorkLocationsFromIds(
  ids: number[] | null | undefined,
  locationsCache?: { items?: GetLocationBaseResponse[] },
): GetLocationBaseResponse[] | undefined {
  if (ids === null) return [];
  if (!Array.isArray(ids) || ids.length === 0) return undefined;
  if (!locationsCache?.items?.length) return undefined;

  return ids
    .map((id) =>
      locationsCache.items!.find(
        (location) => Number(location.locationId) === Number(id),
      ),
    )
    .filter(
      (location): location is GetLocationBaseResponse => location != null,
    );
}

export const useUserUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
  options?: UseUserUpdateOptions,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (updateUserRequest: UserUpdatePayload) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const cleanRequest = {
        userId: updateUserRequest.userId,
        firstName: updateUserRequest.firstName,
        lastName: updateUserRequest.lastName,
        email: updateUserRequest.email,
        cellPhone: updateUserRequest.cellPhone,
        homePhone: updateUserRequest.homePhone ?? null,
        address: updateUserRequest.address ?? null,
        city: updateUserRequest.city ?? null,
        state: updateUserRequest.state ?? null,
        zip: updateUserRequest.zip ?? null,
        country: updateUserRequest.country ?? null,
        gender: updateUserRequest.gender ?? null,
        dateOfBirth: updateUserRequest.dateOfBirth,
        leadSource: updateUserRequest.leadSource ?? null,
        ...(updateUserRequest.locationIds !== undefined
          ? { locationIds: updateUserRequest.locationIds }
          : {}),
        workLocationIds: updateUserRequest.workLocationIds ?? undefined,
        payRate: updateUserRequest.payRate,
        paymentType: updateUserRequest.paymentType,
        payrollId: updateUserRequest.payrollId ?? null,
      } as UpdateUserRequest;
      const service = createAuthenticatedApi(UsersApi, accessToken);

      return service.apiUserPutRaw(
        { updateUserRequest: cleanRequest },
        async ({ init }): Promise<RequestInit> => ({
          body: withUserEmailInPutBody(
            init.body,
            updateUserRequest.email,
          ) as BodyInit,
        }),
      );
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersScrollGet],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UserGet, variables.userId],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.EntityChangeHistory],
      });

      const userQueryKey = [QueryKeys.UserGet, variables.userId];
      const locationsCache = queryClient.getQueryData([QueryKeys.Locations]) as
        | { items?: GetLocationBaseResponse[] }
        | undefined;
      const normalizedWorkLocationIds = normalizeWorkLocationIds(
        variables.workLocationIds,
      );
      const mergedWorkLocations = buildWorkLocationsFromIds(
        normalizedWorkLocationIds,
        locationsCache,
      );

      // Merge workLocations into cached user so single staff page shows updated work locations
      queryClient.setQueryData(userQueryKey, (old: unknown) => {
        if (!old || typeof old !== "object") return old;
        const prev = old as Record<string, unknown>;
        return {
          ...prev,
          ...(variables.email !== undefined && {
            email: variables.email,
          }),
          ...(normalizedWorkLocationIds !== undefined && {
            workLocationIds: normalizedWorkLocationIds,
          }),
          ...(mergedWorkLocations !== undefined && {
            workLocations: mergedWorkLocations,
          }),
        };
      });

      // Merge email/workLocations into cached users list so tables stay in sync
      if (
        normalizedWorkLocationIds !== undefined ||
        variables.email !== undefined
      ) {
        queryClient.setQueriesData(
          { queryKey: [QueryKeys.UsersScrollGet], exact: false },
          (old: unknown) => {
            if (!old || typeof old !== "object" || !("items" in old))
              return old;
            const prev = old as { items?: unknown[] };
            const items = Array.isArray(prev.items) ? prev.items : [];
            const nextItems = items.map((item: unknown) => {
              if (!item || typeof item !== "object" || !("userId" in item))
                return item;
              const user = item as { userId: number };
              if (user.userId !== variables.userId) return item;
              return {
                ...item,
                ...(variables.email !== undefined && {
                  email: variables.email,
                }),
                ...(mergedWorkLocations !== undefined && {
                  workLocations: mergedWorkLocations,
                }),
              };
            });
            return { ...prev, items: nextItems };
          },
        );
      }

      toast({
        title: options?.successTitle ?? "Staff updated",
        description:
          options?.successDescription ?? "Staff member updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to update staff",
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
